import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK } from '@/game/config';
import type { PackClass } from '@/data/packs/types';
import { drawContour, hasContour } from '@/game/contours';
import { speakExplanation, cancelRulePrompt } from '@/game/audio';

interface HelpInit { classDef: PackClass }

export class HelpScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Help' });
  }

  create(data: HelpInit): void {
    this.input.setDefaultCursor('default');
    // Drop the gameplay cursor-hiding class so the system mouse is visible over this
    // read-and-click panel. CSS `body.in-game #game canvas { cursor: none !important }`
    // otherwise beats setDefaultCursor's inline style and leaves the cursor hidden (touch
    // is unaffected, hence the desktop-only bug). Restored on dismiss.
    document.body.classList.remove('in-game');

    // Dim full-screen overlay — present but NOT interactive: the only dismissal path is the
    // "Weiter" button, per game design.
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);

    // Panel.
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.85, GAME_HEIGHT * 0.78, 0x1a1f25)
      .setStrokeStyle(4, 0xffd166);

    // Title — the class label.
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 210, data.classDef.label, {
      fontFamily: FONT_STACK,
      fontSize: '64px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);

    // Tonal packs: show the pitch-contour glyph (the arrow shape on the banner) so the help
    // panel teaches what the tone's pitch looks like, blown up larger than the in-game banner.
    if (hasContour(data.classDef.contour)) {
      const glyph = drawContour(this, data.classDef.contour!);
      if (glyph) glyph.setPosition(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 130).setScale(2.4);
    }

    // Explanation body — word-wrapped, centered.
    const explanation = data.classDef.explanation ?? '';
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, explanation, {
      fontFamily: FONT_STACK,
      fontSize: '28px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.72 },
      lineSpacing: 8,
    }).setOrigin(0.5);

    // Read the explanation aloud. It is several sentences of small type — more than a child who is
    // still learning to read can get through on their own, which is exactly who opens this panel.
    // The title is spoken first so the readout names the class before describing it.
    // No replay control by design: the way to hear it again is to close the panel and tap "?" once
    // more, which re-runs create() and starts this readout from the top.
    speakExplanation(`${data.classDef.label}. ${explanation}`, i18next.language);

    // The "Weiter" button. Dismissable by tap/click and by keyboard (Enter/Space/Esc).
    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200, i18next.t('nav.continue'), {
      fontFamily: FONT_STACK,
      fontSize: '36px', color: '#ffffff',
      backgroundColor: '#7ec4ff', padding: { x: 40, y: 14 },
      fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;          // guard: pointerup+pointerdown / repeated keys fire once
      dismissed = true;
      // Closing the panel stops the readout mid-sentence — the child has moved on, and a voice
      // still explaining over a resumed board would talk across the round's own banner readout.
      cancelRulePrompt();
      this.scene.stop();
      this.scene.resume('Round');
      this.scene.resume('Board');
      this.scene.resume('HUD');
      // BoardScene set cursor:none on create; HelpScene reset to default — restore.
      document.body.classList.add('in-game');
      this.scene.get('Board').input.setDefaultCursor('none');
    };

    // pointerup (not just pointerdown): the help overlay is launched from inside a pointerdown
    // on the board's "?" button, which leaves the persistent mouse pointer in a stale state so
    // the new scene misses its first pointerdown. Touch uses a fresh pointer so it worked; pointerup
    // is reliable for both. Keep pointerdown too for snappy touch response.
    cont.on('pointerup', dismiss);
    cont.on('pointerdown', dismiss);

    // Keyboard dismissal — Enter/Space (continue) and Esc (close).
    this.input.keyboard?.on('keydown-ENTER', dismiss);
    this.input.keyboard?.on('keydown-SPACE', dismiss);
    this.input.keyboard?.on('keydown-ESC', dismiss);
  }
}
